import { useRegisterFeedbackContext } from "@appretio/feedback";
import { SelectGroup } from "@appretio/ui-kit";
import React, { useCallback } from "react";
import {
  type ContextField,
  type ContextSelectedOptions
} from "./contextConfig";
import { ContextDropdown } from "./ContextDropdown";

export type ContextOptionsByField = Partial<
  Record<ContextField, string[] | null>
>;

interface ContextSelectorProps<
  TSelected extends Record<string, string[]> | undefined =
    ContextSelectedOptions
> {
  /**
   * List of dropwdowns/context fields to display
   */
  fields: ContextField[];
  /**
   * Currently selected options for each context field
   */
  selected: TSelected;
  /**
   * Sets selected options for each context field
   */
  setSelected: React.Dispatch<React.SetStateAction<TSelected>>;
  /**
   * Base context options data (all available options)
   */
  baseContextOptions?: ContextOptionsByField;
  /**
   * Filtered context options data (options filtered by current selection)
   */
  filteredContextOptions?: ContextOptionsByField;
  /**
   * Whether the data is currently loading
   */
  isLoading?: boolean;
  /**
   * Disables all dropdown triggers in the group.
   */
  disabled?: boolean;
  /**
   * Individual fields to disable while the rest stay editable — e.g. a picker
   * inside a modal that shows, but can't change, the axes its host page has
   * committed.
   */
  disabledFields?: ContextField[];
  /**
   * Tooltip on each of {@link disabledFields} saying why it can't change here.
   */
  disabledFieldsReason?: string;
  /**
   * Fields to highlight as invalid — set when an action was blocked because
   * these fields have no selection.
   */
  errorFields?: ContextField[];
  /**
   * Render each dropdown as a modal popover — required when the selector lives
   * inside a modal Dialog, otherwise the dialog's scroll lock swallows wheel
   * events on the option lists.
   */
  modalPopover?: boolean;
}

export function ContextSelector<
  TSelected extends Record<string, string[]> | undefined =
    ContextSelectedOptions
>({
  fields,
  selected,
  setSelected,
  baseContextOptions,
  filteredContextOptions,
  isLoading = false,
  disabled,
  disabledFields,
  disabledFieldsReason,
  errorFields,
  modalPopover
}: ContextSelectorProps<TSelected>) {
  // Feedback capture: every page that renders the selector contributes the
  // live (possibly not-yet-applied) dropdown selections to feedback reports.
  // Pages add their own applied context / dirty flag on top — the blocks
  // merge one level deep under `top_bar`. No-op outside a FeedbackProvider.
  useRegisterFeedbackContext(() => ({
    top_bar: {
      selector_fields: fields,
      selector_draft: selected ?? {},
      selector_options_loading: isLoading
    }
  }));

  // Memoize onChange handler
  const handleSetSelected = useCallback(
    (field: ContextField, value: string[]) => {
      setSelected((prev) => {
        if (value.length === 0) {
          const rest = { ...prev };
          if (rest) {
            delete rest[field];
          }
          return rest;
        }

        return { ...prev, [field]: value };
      });
    },
    [setSelected]
  );

  return (
    <SelectGroup>
      {fields.map((field) => {
        const options =
          (baseContextOptions as ContextOptionsByField | undefined)?.[field] ??
          [];
        const filteredOptions =
          (filteredContextOptions as ContextOptionsByField | undefined)?.[
            field
          ] ?? [];
        const selectedOptions = selected?.[field] ?? [];
        const fieldDisabled = disabled || disabledFields?.includes(field);

        return (
          <ContextDropdown
            key={field}
            field={field}
            options={options}
            filteredOptions={filteredOptions}
            isLoading={isLoading}
            selected={selectedOptions}
            setSelected={handleSetSelected}
            disabled={fieldDisabled}
            disabledReason={
              fieldDisabled && !disabled ? disabledFieldsReason : undefined
            }
            error={errorFields?.includes(field)}
            modalPopover={modalPopover}
          />
        );
      })}
    </SelectGroup>
  );
}
